import { createGuestSession } from "@/shared/api/auth";
import { getAttributionForAuth } from "@/shared/lib/attributionUtm";
/**
 * Гостевой чекаут: гостевая сессия «вечная» — сервер ставит httpOnly-куки
 * (access + refresh) с TTL ~10 лет, привязанные к браузеру. Отдельной клиентской
 * guest-куки/TTL у фронта нет — сроком рулит сервер; refresh для гостя
 * перевыпускает такую же долгоживущую пару. Потеря куки = новый гость
 * (fingerprint собираем как анти-абуз, но для ВОССТАНОВЛЕНИЯ сессии не
 * используем — это осознанное решение).
 *
 * Решение «переиспользовать или открыть новую» принимается НЕ здесь, а по
 * ответу GET /users/me на старте (см. AuthProvider): 200 + is_guest=false —
 * аккаунт; 200 + is_guest=true (legacy 403) — живой гость, переиспользуем его
 * куки; 401/нет токена — гостя нет или рефреш протух → открываем новую сессию.
 *
 * Здесь храним только in-memory защиту от дублей в рамках одной загрузки
 * (React StrictMode дважды вызывает эффекты; без этого открылись бы две
 * гостевые сессии подряд). На полной перезагрузке состояние сбрасывается, и
 * решение снова принимает /users/me.
 */
let sessionActive = false;
let creationPromise = null;
/** click_id, уже ушедший на бэк в этой загрузке (антидубль StrictMode). */
let lastSyncedClickId = null;
/**
 * Лёгкий fingerprint браузера: набор стабильных сигналов без сторонних
 * библиотек. Уходит на бэк как fingerprint_components, а его компактный хэш —
 * как поле fingerprint (бэк ограничивает его 256 символами).
 */
function buildFingerprintComponents() {
    try {
        const nav = navigator;
        return {
            user_agent: nav.userAgent,
            language: nav.language,
            languages: Array.isArray(nav.languages) ? nav.languages : undefined,
            platform: nav.userAgentData?.platform ?? nav.platform,
            screen: `${window.screen.width}x${window.screen.height}x${window.screen.colorDepth}`,
            viewport: `${window.innerWidth}x${window.innerHeight}`,
            device_pixel_ratio: window.devicePixelRatio,
            timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
            timezone_offset: new Date().getTimezoneOffset(),
            hardware_concurrency: nav.hardwareConcurrency,
            device_memory: nav.deviceMemory,
            max_touch_points: nav.maxTouchPoints,
        };
    }
    catch {
        return {};
    }
}
/** Детерминированный 32-битный хэш (FNV-1a) компонентов → короткая hex-строка. */
function hashFingerprint(components) {
    try {
        const raw = JSON.stringify(components);
        let h = 0x811c9dc5;
        for (let i = 0; i < raw.length; i++) {
            h ^= raw.charCodeAt(i);
            h = Math.imul(h, 0x01000193);
        }
        const hex = (h >>> 0).toString(16).padStart(8, "0");
        return `${hex}${raw.length.toString(16)}`;
    }
    catch {
        return "";
    }
}
/**
 * Открывает НОВУЮ гостевую сессию для неавторизованного веб-посетителя.
 * Вызывать только когда живой гостевой сессии нет (см. AuthProvider: /users/me
 * вернул 401/нет токена). Дубли в рамках одной загрузки схлопываются
 * (StrictMode). Возвращает true, если сессия активна. Ошибку глушим — без
 * гостевой сессии часть ручек вернёт 401/403, но приложение не должно падать.
 */
export async function ensureGuestSession() {
    if (typeof window === "undefined") {
        return false;
    }
    if (sessionActive) {
        return true;
    }
    if (creationPromise) {
        return creationPromise;
    }
    creationPromise = (async () => {
        try {
            const components = buildFingerprintComponents();
            const fingerprint = hashFingerprint(components);
            await createGuestSession({
                ...(fingerprint ? { fingerprint } : {}),
                fingerprint_components: components,
            });
            const clickId = getAttributionForAuth().click_id;
            if (clickId) {
                lastSyncedClickId = clickId;
            }
            sessionActive = true;
            return true;
        }
        catch (e) {
            console.error("Guest session creation failed:", e);
            return false;
        }
        finally {
            creationPromise = null;
        }
    })();
    return creationPromise;
}
/**
 * Помечает, что живая гостевая сессия уже есть (например, /users/me вернул 403).
 * Тогда ensureGuestSession() не будет открывать новую в этой загрузке.
 */
export function markGuestSessionActive() {
    sessionActive = true;
}
/**
 * Повторный рекламный заход при живой гостевой куке (~7 дней): раньше
 * POST /auth/guest не вызывался → новый click_id не уходил на бэк.
 * Куки идентифицируют гостя; бэк привязывает click_id к существующей сессии.
 * Без click_id — no-op.
 */
export async function syncGuestAttributionIfNeeded() {
    if (typeof window === "undefined") {
        return;
    }
    const clickId = getAttributionForAuth().click_id;
    if (!clickId || clickId === lastSyncedClickId) {
        return;
    }
    try {
        await createGuestSession();
        lastSyncedClickId = clickId;
    }
    catch (e) {
        console.error("Guest attribution sync failed:", e);
    }
}
/** Сбрасывает признак гостевой сессии (например, при выходе из аккаунта). */
export function clearGuestSessionFlag() {
    sessionActive = false;
    creationPromise = null;
    lastSyncedClickId = null;
}
